import { Action, Reducer } from 'redux';
import { AppThunkAction } from '..';
import {
  getRequestOptions,
  postRequestOptions,
  getDateWithoutTimezone,
  postFormRequestOptions,
} from '../../utils/utils';
import { FetchError } from '../../Interfaces/FetchError';
import {
  AssetStateRecord,
  AssetStateQueryResult,
} from '../../Interfaces/Assets/AssetState';
import { CategoryCountRequest } from '../../Interfaces/Assets/CategoryCountRequest';
import { CommandInfo } from '../../Interfaces/Commands/CommandInfo';
import { AssetStateCountResponse } from '../../Interfaces/Assets/AssetStateCountResponse';
import { NodeStatusCount } from '../../Interfaces/Assets/NodeStatusCount';
import { AssetStateCountBySite } from '../../Interfaces/Assets/AssetStateCountBySite';
import { CategorySummary } from '../../Interfaces/Assets/CategorySummary';
import { AssetState } from '../../Interfaces/Assets/AssetState';
import { AssetStatePropertiesCountsByDateRange } from '../../Interfaces/Devices/Trends/AssetStatePropertiesCountsByDateRange';
import {
  CurrentIssueByDateCount,
  CurrentIssueCountsByDate,
} from '../../Interfaces/Devices/Trends/CurrentIssueCountsByDate';
import {
  TrendsGridFilter,
  TrendsGridFilterPayload,
} from '../../Interfaces/Devices/Trends/TrendsGridFilter';

import { ApiEnums } from '../../Interfaces/ApiEnums';
import { DeviceIssueTrendsCSV } from '../../Interfaces/Devices/Trends/DeviceIssueTrendsCSV';
import { TrendsFilterPayload } from '../../Interfaces/Devices/Trends/TrendsFilter';
import {
  FirmwareFileAndChecksumMap,
  FirmwareInfo,
  MultipleFirmwareRecords,
} from '../../Interfaces/Devices/FirmwareInfo';
import { CurrentIssuePropertiesCSV } from '../../Interfaces/Devices/Trends/CurrentIssuePropertiesCSV';

export enum AssetStatusActionTypes {
  RESET_STATE = 'RESET_STATE',
  UNAUTHORIZED_REQUEST = 'UNAUTHORIZED_REQUEST',
  REQUEST_NODE_ASSET_STATUSES = 'REQUEST_NODE_ASSET_STATUSES',
  REQUEST_NODE_ASSET_STATUSES_BY_NODE = 'REQUEST_NODE_ASSET_STATUSES_BY_NODE',
  RECEIVED_NODE_ASSET_STATUSES_BY_NODE = 'RECEIVED_NODE_ASSET_STATUSES_BY_NODE',
  RECEIVED_NODE_ASSET_STATUSES_BY_QUERY = 'RECEIVED_NODE_ASSET_STATUSES_BY_QUERY',

  REQUEST_CURRENT_ISSUE_CATEGORIES = ' REQUEST_CURRENT_ISSUE_CATEGORIES',
  RECEIVED_CURRENT_ISSUE_CATEGORIES = 'RECEIVED_CURRENT_ISSUE_CATEGORIES',

  REQUEST_CURRENT_ISSUE_VALUES = 'REQUEST_CURRENT_ISSUE_VALUES',
  RECEIVED_CURRENT_ISSUE_VALUES = 'RECEIVED_CURRENT_ISSUE_VALUES',

  RECEIVED_HTTP_RESPONSE_ERROR_CODE = 'RECEIVED_HTTP_RESPONSE_ERROR_CODE',
  RESET_HTTP_RESPONSE_FETCH_ERROR = 'RESET_HTTP_RESPONSE_FETCH_ERROR',
  NOTIFICATION_SUCCESS_MESSAGE = 'NOTIFICATION_SUCCESS_MESSAGE',
  REQUEST_ASSET_STATUSES_NAME_COUNT = 'REQUEST_ASSET_STATUSES_NAME_COUNT',
  RECEIVED_ASSET_STATUSES_NAME_COUNT = 'RECEIVED_ASSET_STATUSES_NAME_COUNT',

  REQUEST_NODE_STATUSES_COUNT = 'REQUEST_NODE_STATUSES_COUNT',
  RECEIVED_NODE_STATUSES_COUNT = 'RECEIVED_NODE_STATUSES_COUNT',

  REQUEST_SITE_ISSUES_COUNT = 'REQUEST_SITE_ISSUES_COUNT',
  RECEIVED_SITE_ISSUES_COUNT = 'RECEIVED_SITE_ISSUES_COUNT',

  REQUEST_SITE_LANES_DOWN_COUNT = 'REQUEST_SITE_LANES_DOWN_COUNT',
  RECEIVED_SITE_LANES_DOWN_COUNT = 'RECEIVED_SITE_LANES_DOWN_COUNT',

  REQUEST_CATEGORY_SUMMARY = 'REQUEST_CATEGORY_SUMMARY',
  RECEIVED_CATEGORY_SUMMARY = 'RECEIVED_CATEGORY_SUMMARY',

  REQUEST_ASSET_STATE_PROPERTIES_COUNTS_BY_DATE_RANGE = 'REQUEST_ASSET_STATE_PROPERTIES_COUNTS_BY_DATE_RANGE',
  RECEIVED_ASSET_STATE_PROPERTIES_COUNTS_BY_DATE_RANGE = 'RECEIVED_ASSET_STATE_PROPERTIES_COUNTS_BY_DATE_RANGE',

  REQUEST_ASSET_STATE_PROPERTIES_COUNTS_BY_DATE = 'REQUEST_ASSET_STATE_PROPERTIES_COUNTS_BY_DATE',
  RECEIVED_ASSET_STATE_PROPERTIES_COUNTS_BY_DATE = 'RECEIVED_ASSET_STATE_PROPERTIES_COUNTS_BY_DATE',

  NOTIFICATION_REBOOT_SUCCESS_MESSAGE = 'NOTIFICATION_REBOOT_SUCCESS_MESSAGE',

  REQUEST_FIRMWARE_INFO = 'REQUEST_FIRMWARE_INFO',
  RECEIVED_FIRMWARE_INFO = 'RECEIVED_FIRMWARE_INFO',
  REQUEST_ADD_NEW_FIRMWARE_INFO = 'REQUEST_ADD_NEW_FIRMWARE_INFO',
  RECEIVED_ADD_NEW_FIRMWARE_INFO = 'RECEIVED_ADD_NEW_FIRMWARE_INFO',
  REQUEST_UPDATE_FIRMWARE_FILE = 'REQUEST_UPDATE_FIRMWARE_FILE',
  RECEIVED_UPDATE_FIRMWARE_FILE = 'RECEIVED_UPDATE_FIRMWARE_FILE',
  REQUEST_REMOVE_FIRMWARE_INFO = 'REQUEST_REMOVE_FIRMWARE_INFO',
  RECEIVED_REMOVE_FIRMWARE_INFO = 'RECEIVED_REMOVE_FIRMWARE_INFO',
  REQUEST_FILE_CHECKSUM = 'REQUEST_FILE_CHECKSUM',
  RECEIVED_FILE_CHECKSUM = 'RECEIVED_FILE_CHECKSUM',
  REQUEST_UPLOAD_FIRMWARE_FILE = 'REQUEST_UPLOAD_FIRMWARE_FILE',
  UPDATE_CONFIRMATION_MESSAGE = 'UPDATE_CONFIRMATION_MESSAGE',
  CLEAR_CONFIRMATION_MESSAGE = 'CLEAR_CONFIRMATION_MESSAGE',
  CLEAR_CHECKSUM = 'CLEAR_CHECKSUM',
  REGISTRATION_SUCCESS_MESSAGE = 'REGISTRATION_SUCCESS_MESSAGE',
  UPDATE_FILE_SUCCESS_MESSAGE = 'UPDATE_FILE_SUCCESS_MESSAGE',
  DELETE_FILE_SUCCESS_MESSAGE = 'DELETE_FILE_SUCCESS_MESSAGE',

  REQUEST_ASSET_STATE_PROPERTIES_DETAILS_BY_DATE_RANGE = 'REQUEST_ASSET_STATE_PROPERTIES_DETAILS_BY_DATE_RANGE',
  RECEIVED_ASSET_STATE_PROPERTIES_DETAILS_BY_DATE_RANGE = 'RECEIVED_ASSET_STATE_PROPERTIES_DETAILS_BY_DATE_RANGE',
}

// -----------------
// STATE - This defines the type of data maintained in the Redux store.
export interface AssetStatusesState {
  unauthorizedRequest: boolean;
  isFetchingAssetStatuses: boolean;
  assetStatuses: AssetStateRecord[];
  currentIssueCategories: string[];
  isFetchingCurrentIssueCategories: boolean;
  currentIssueValues: Map<CategoryCountRequest, string[]>;
  isFetchingCurrentIssueValues: boolean;
  fetchError: FetchError;
  notificationSuccessMessage: string;
  isFetchingAssetStatusCounts: boolean;
  assetStatusCounts: Map<string, AssetStateCountResponse[]>;
  isFetchingNodeStatusCounts: boolean;
  nodeStatusCounts: NodeStatusCount[];
  isFetchingDeviceIssuesCountBySite: boolean;
  deviceIssuesCountBySite: AssetStateCountBySite[];
  isFetchingLanesDownCountBySite: boolean;
  lanesDownCountBySite: AssetStateCountBySite[];
  isFetchingCategorySummary: boolean;
  categorySummary: CategorySummary[];
  isFetchingAssetStatusesByNode: boolean;
  assetStateProperties: AssetState[];
  isFetchingAssetStatePropertiesCountsByDateRange: boolean;
  assetStatePropertiesCountsByDateRange: AssetStatePropertiesCountsByDateRange;
  isFetchingCurrentIssueCountsByDate: boolean;
  currentIssuesByDate: DeviceIssueTrendsCSV[];
  currentIssueCount: number;
  notificationRebootSuccessMessage: string;
  firmwareInfo: FirmwareInfo[];
  isFetchingCurrentIssuePropertiesCSVData: boolean;
  currentIssuePropertiesCSVData: CurrentIssuePropertiesCSV[];
  fileChecksum: string[];
  fileChecksumMap: FirmwareFileAndChecksumMap[];
  filesUploaded: string[];
  confirmationMessage: string;
  registrationSuccessMessage: string;
  updateSuccessMessage: string;
  deleteSuccessMessage: string;
}

// -----------------
// ACTIONS - These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects; they just describe something that is going to happen.
interface ResetStateAction {
  type: AssetStatusActionTypes.RESET_STATE;
}

interface UnauthorizedRequestAction {
  type: AssetStatusActionTypes.UNAUTHORIZED_REQUEST;
}

interface RequestNodeAssetStatusesAction {
  type: AssetStatusActionTypes.REQUEST_NODE_ASSET_STATUSES;
}

interface RequestNodeAssetStatusesByNodeAction {
  type: AssetStatusActionTypes.REQUEST_NODE_ASSET_STATUSES_BY_NODE;
}

interface ReceivedNodeAssetStatusesByNodeAction {
  type: AssetStatusActionTypes.RECEIVED_NODE_ASSET_STATUSES_BY_NODE;
  nodeId: number;
  assetStateProperties: AssetState[];
}

interface ReceivedNodeAssetStatusesByQueryAction {
  type: AssetStatusActionTypes.RECEIVED_NODE_ASSET_STATUSES_BY_QUERY;
  result: AssetStateQueryResult;
}

interface GetCurrentIssueCategoriesAction {
  type: AssetStatusActionTypes.RECEIVED_CURRENT_ISSUE_CATEGORIES;
  categories: string[];
}

interface RequestCurrentIssueCategoriesAction {
  type: AssetStatusActionTypes.REQUEST_CURRENT_ISSUE_CATEGORIES;
  refreshing: boolean;
}

interface RequestCurrentIssueValuesAction {
  type: AssetStatusActionTypes.REQUEST_CURRENT_ISSUE_VALUES;
  refreshing: boolean;
}

interface ReceivedCurrentIssueValuesAction {
  type: AssetStatusActionTypes.RECEIVED_CURRENT_ISSUE_VALUES;
  categoryCountRequest: CategoryCountRequest;
  values: string[];
}

interface ReceivedErrorCode {
  type: AssetStatusActionTypes.RECEIVED_HTTP_RESPONSE_ERROR_CODE;
  statusCode: number;
}

interface ResetHttpResponseError {
  type: AssetStatusActionTypes.RESET_HTTP_RESPONSE_FETCH_ERROR;
}

interface NotificationSuccessMessage {
  type: AssetStatusActionTypes.NOTIFICATION_SUCCESS_MESSAGE;
  message: string;
}

interface RequestAssetStatusesNameCount {
  type: AssetStatusActionTypes.REQUEST_ASSET_STATUSES_NAME_COUNT;
  refreshing: boolean;
}

interface ReceivedAssetStatusesNameCount {
  type: AssetStatusActionTypes.RECEIVED_ASSET_STATUSES_NAME_COUNT;
  result: Map<string, AssetStateCountResponse[]>;
}

interface RequestNodeStatusesCount {
  type: AssetStatusActionTypes.REQUEST_NODE_STATUSES_COUNT;
  refreshing: boolean;
}

interface ReceivedNodeStatusesCount {
  type: AssetStatusActionTypes.RECEIVED_NODE_STATUSES_COUNT;
  result: NodeStatusCount[];
}

interface RequestSiteIssuesCount {
  type: AssetStatusActionTypes.REQUEST_SITE_ISSUES_COUNT;
  refreshing: boolean;
}

interface ReceivedSiteIssuesCount {
  type: AssetStatusActionTypes.RECEIVED_SITE_ISSUES_COUNT;
  result: AssetStateCountBySite[];
}

interface RequestSiteLanesDownCount {
  type: AssetStatusActionTypes.REQUEST_SITE_LANES_DOWN_COUNT;
  refreshing: boolean;
}

interface ReceivedSiteLanesDownCount {
  type: AssetStatusActionTypes.RECEIVED_SITE_LANES_DOWN_COUNT;
  result: AssetStateCountBySite[];
}

interface RequestCategorySummary {
  type: AssetStatusActionTypes.REQUEST_CATEGORY_SUMMARY;
  refreshing: boolean;
}

interface ReceivedCategorySummary {
  type: AssetStatusActionTypes.RECEIVED_CATEGORY_SUMMARY;
  categorySummary: CategorySummary[];
}

interface RequestAssetStatePropertiesCountsByDateRange {
  type: AssetStatusActionTypes.REQUEST_ASSET_STATE_PROPERTIES_COUNTS_BY_DATE_RANGE;
  refreshing: boolean;
}

interface ReceivedAssetStatePropertiesCountsByDateRange {
  type: AssetStatusActionTypes.RECEIVED_ASSET_STATE_PROPERTIES_COUNTS_BY_DATE_RANGE;
  assetStatePropertiesCountsByDateRange: AssetStatePropertiesCountsByDateRange;
}

interface RequestCurrentIssueCountsByDate {
  type: AssetStatusActionTypes.REQUEST_ASSET_STATE_PROPERTIES_COUNTS_BY_DATE;
  refreshing: boolean;
}

interface ReceivedCurrentIssueCountsByDate {
  type: AssetStatusActionTypes.RECEIVED_ASSET_STATE_PROPERTIES_COUNTS_BY_DATE;
  currentIssueCountsByDate: DeviceIssueTrendsCSV[];
  count: number;
}

interface NotificationRebootSuccessMessage {
  type: AssetStatusActionTypes.NOTIFICATION_REBOOT_SUCCESS_MESSAGE;
  message: string;
}

interface RequestFirmwareInfo {
  type: AssetStatusActionTypes.REQUEST_FIRMWARE_INFO;
}

interface ReceivedFirmwareInfo {
  type: AssetStatusActionTypes.RECEIVED_FIRMWARE_INFO;
  firmware: FirmwareInfo[];
}

interface RequestedAddNewFirmwareInfo {
  type: AssetStatusActionTypes.REQUEST_ADD_NEW_FIRMWARE_INFO;
}

interface ReceivedAddNewFirmwareInfo {
  type: AssetStatusActionTypes.RECEIVED_ADD_NEW_FIRMWARE_INFO;
  firmware: FirmwareInfo;
}

interface RequestedUpdateFirmwareFile {
  type: AssetStatusActionTypes.REQUEST_UPDATE_FIRMWARE_FILE;
}

interface ReceivedUpdateFirmwareFile {
  type: AssetStatusActionTypes.RECEIVED_UPDATE_FIRMWARE_FILE;
  firmware: FirmwareInfo;
}

interface RequestedRemoveFirmwareInfo {
  type: AssetStatusActionTypes.REQUEST_REMOVE_FIRMWARE_INFO;
}

interface ReceivedRemoveFirmwareInfo {
  type: AssetStatusActionTypes.RECEIVED_REMOVE_FIRMWARE_INFO;
  firmware: FirmwareInfo;
}

interface RequestFileChecksum {
  type: AssetStatusActionTypes.REQUEST_FILE_CHECKSUM;
}

interface ReceivedFileChecksum {
  type: AssetStatusActionTypes.RECEIVED_FILE_CHECKSUM;
  fileChecksumMap: FirmwareFileAndChecksumMap[];
}

interface ReceivedConfirmationMessage {
  type: AssetStatusActionTypes.UPDATE_CONFIRMATION_MESSAGE;
  message: string;
}

interface ClearConfirmationMessage {
  type: AssetStatusActionTypes.CLEAR_CONFIRMATION_MESSAGE;
  message: string;
}

interface ClearChecksumMessage {
  type: AssetStatusActionTypes.CLEAR_CHECKSUM;
  fileChecksumMap: FirmwareFileAndChecksumMap[];
}

interface RegistrationSuccessMessageAction {
  type: AssetStatusActionTypes.REGISTRATION_SUCCESS_MESSAGE;
  message: string;
  filesUploaded: string[];
}

interface UpdateFileSuccessMessageAction {
  type: AssetStatusActionTypes.UPDATE_FILE_SUCCESS_MESSAGE;
  message: string;
}

interface DeleteFileSuccessMessageAction {
  type: AssetStatusActionTypes.DELETE_FILE_SUCCESS_MESSAGE;
  message: string;
}

interface RequestAssetStatePropertiesDetailsByDateRange {
  type: AssetStatusActionTypes.REQUEST_ASSET_STATE_PROPERTIES_DETAILS_BY_DATE_RANGE;
  refreshing: boolean;
}

interface ReceivedAssetStatePropertiesDetailsByDateRange {
  type: AssetStatusActionTypes.RECEIVED_ASSET_STATE_PROPERTIES_DETAILS_BY_DATE_RANGE;
  data: CurrentIssuePropertiesCSV[];
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ResetStateAction
  | UnauthorizedRequestAction
  | RequestNodeAssetStatusesAction
  | RequestNodeAssetStatusesByNodeAction
  | ReceivedNodeAssetStatusesByNodeAction
  | ReceivedNodeAssetStatusesByQueryAction
  | GetCurrentIssueCategoriesAction
  | RequestCurrentIssueCategoriesAction
  | RequestCurrentIssueValuesAction
  | ReceivedCurrentIssueValuesAction
  | ReceivedErrorCode
  | NotificationSuccessMessage
  | ResetHttpResponseError
  | RequestAssetStatusesNameCount
  | ReceivedAssetStatusesNameCount
  | RequestNodeStatusesCount
  | ReceivedNodeStatusesCount
  | RequestSiteIssuesCount
  | ReceivedSiteIssuesCount
  | RequestSiteLanesDownCount
  | ReceivedSiteLanesDownCount
  | RequestCategorySummary
  | ReceivedCategorySummary
  | RequestCategorySummary
  | ReceivedCategorySummary
  | RequestAssetStatePropertiesCountsByDateRange
  | ReceivedAssetStatePropertiesCountsByDateRange
  | RequestCurrentIssueCountsByDate
  | ReceivedCurrentIssueCountsByDate
  | NotificationRebootSuccessMessage
  | RequestFirmwareInfo
  | ReceivedFirmwareInfo
  | RequestedAddNewFirmwareInfo
  | ReceivedAddNewFirmwareInfo
  | RequestedUpdateFirmwareFile
  | ReceivedUpdateFirmwareFile
  | RequestedRemoveFirmwareInfo
  | ReceivedRemoveFirmwareInfo
  | RequestFileChecksum
  | ReceivedFileChecksum
  | ReceivedConfirmationMessage
  | ClearConfirmationMessage
  | ClearChecksumMessage
  | RegistrationSuccessMessageAction
  | UpdateFileSuccessMessageAction
  | DeleteFileSuccessMessageAction
  | RequestAssetStatePropertiesDetailsByDateRange
  | ReceivedAssetStatePropertiesDetailsByDateRange;

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).
const isResponseOk = (response: Response, dispatch: any): boolean => {
  if (response.ok) return true;
  if (response.status === 401) {
    dispatch({
      type: AssetStatusActionTypes.UNAUTHORIZED_REQUEST,
    });
  } else {
    dispatch({
      type: AssetStatusActionTypes.RECEIVED_HTTP_RESPONSE_ERROR_CODE,
      statusCode: response.status,
    });
  }
  return false;
};

const convertCurrentIssueCountsToCSV = (
  nodes: CurrentIssueByDateCount[]
): DeviceIssueTrendsCSV[] => {
  let result: DeviceIssueTrendsCSV[] = [];

  if (Array.isArray(nodes)) {
    nodes.forEach(node => {
      node.NodeInfo.CurrentIssueProperties.forEach(currentIssue => {
        let data: DeviceIssueTrendsCSV = {
          Division: node.NodeInfo.Division,
          SiteNumber: node.NodeInfo.SiteNumber,
          NodeGroup: node.NodeInfo.NodeGroup,
          LaneNumber: node.NodeInfo.LaneNumber,
          Category: currentIssue.Category,
          Model: currentIssue.Model,
          Value: currentIssue.Value,
          Description: currentIssue.Description,
          FirmwareVersion: currentIssue.FirmwareVersion,
          SerialNumber: currentIssue.SerialNumber,
          RegionInfo: currentIssue.RegionInfo,
		  MachineName: node.NodeInfo.MachineName,
          CreatedOn: currentIssue.CreatedOn,
          Date: currentIssue.TimeStamp,
          AgeDays: currentIssue.AgeDays,
        };

        result.push(data);
      });
    });
  }

  return result;
};

export const actionCreators = {
  resetState: (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
    const appState = getState();
    if (!appState) return;

    console.log(`Dispatching ${AssetStatusActionTypes.RESET_STATE}`);

    dispatch({
      type: AssetStatusActionTypes.RESET_STATE,
    });
  },

  fetchAssetStatePropertiesByNode:
    (nodeId: number): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${AssetStatusActionTypes.REQUEST_NODE_ASSET_STATUSES_BY_NODE}`
      );
      dispatch({
        type: AssetStatusActionTypes.REQUEST_NODE_ASSET_STATUSES_BY_NODE,
      });

      const response = await fetch(
        `assets/GetAssetStatePropertiesByNode/${nodeId}`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as AssetState[];

      console.log(
        `Dispatching ${AssetStatusActionTypes.RECEIVED_NODE_ASSET_STATUSES_BY_NODE}`
      );

      dispatch({
        type: AssetStatusActionTypes.RECEIVED_NODE_ASSET_STATUSES_BY_NODE,
        nodeId: nodeId,
        assetStateProperties: result,
      });
    },

  fetchAssetStatusesByQuery:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${AssetStatusActionTypes.REQUEST_NODE_ASSET_STATUSES}`
      );
      dispatch({
        type: AssetStatusActionTypes.REQUEST_NODE_ASSET_STATUSES,
      });

      const response = await fetch(
        `assets/GetAssetStatePropertiesByQuery`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          appState.userSettingsReducer.assetStatusesFilters
        )
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as AssetStateQueryResult;

      console.log(
        `Dispatching ${AssetStatusActionTypes.RECEIVED_NODE_ASSET_STATUSES_BY_QUERY}`
      );

      dispatch({
        type: AssetStatusActionTypes.RECEIVED_NODE_ASSET_STATUSES_BY_QUERY,
        result: result,
      });
    },

  getCurrentIssueCategories:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${AssetStatusActionTypes.REQUEST_CURRENT_ISSUE_CATEGORIES}`
      );

      dispatch({
        type: AssetStatusActionTypes.REQUEST_CURRENT_ISSUE_CATEGORIES,
        refreshing: true,
      });

      const response = await fetch(
        `assets/GetCurrentIssueCategories`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as string[];

      console.log(
        `Dispatching ${AssetStatusActionTypes.RECEIVED_CURRENT_ISSUE_CATEGORIES}`
      );

      dispatch({
        type: AssetStatusActionTypes.RECEIVED_CURRENT_ISSUE_CATEGORIES,
        categories: result,
      });
    },

  getCurrentIssuesForCategory:
    (categoryCountRequest: CategoryCountRequest): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      dispatch({
        type: AssetStatusActionTypes.REQUEST_CURRENT_ISSUE_VALUES,
        refreshing: true,
      });

      const response = await fetch(
        `assets/GetCurrentIssueValues`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          categoryCountRequest
        )
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as string[];

      console.log(
        `Dispatching ${AssetStatusActionTypes.RECEIVED_CURRENT_ISSUE_VALUES}`
      );

      dispatch({
        type: AssetStatusActionTypes.RECEIVED_CURRENT_ISSUE_VALUES,
        categoryCountRequest: categoryCountRequest,
        values: result,
      });
    },

  sendDeviceCommand:
    (nodeID: number, commandInfo: CommandInfo): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      const response = await fetch(
        `CommandResponse/sendDeviceCommand/${nodeID}`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          commandInfo
        )
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      console.log(`Dispatched command to ${nodeID}`, commandInfo);

      if (commandInfo.Command === 'ResetDevice') {
        dispatch({
          type: AssetStatusActionTypes.NOTIFICATION_SUCCESS_MESSAGE,
          message: 'COMMAND_RESET_DEVICE_SENT_SUCCESS',
        });
      } else if (commandInfo.Command === 'Reboot') {
        dispatch({
          type: AssetStatusActionTypes.NOTIFICATION_REBOOT_SUCCESS_MESSAGE,
          message: 'COMMAND_REBOOT_DEVICE_SENT_SUCCESS',
        });
      }
    },

  resetAssetStatusesFetchError:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${AssetStatusActionTypes.RESET_HTTP_RESPONSE_FETCH_ERROR}`
      );

      dispatch({
        type: AssetStatusActionTypes.RESET_HTTP_RESPONSE_FETCH_ERROR,
      });
    },

  clearCommandNotificationSuccessMessage:
    (commandInfo: CommandInfo | undefined): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${AssetStatusActionTypes.NOTIFICATION_SUCCESS_MESSAGE}`
      );

      if (commandInfo?.Command === 'ResetDevice')
        dispatch({
          type: AssetStatusActionTypes.NOTIFICATION_SUCCESS_MESSAGE,
          message: '',
        });
      else if (commandInfo?.Command === 'Reboot')
        dispatch({
          type: AssetStatusActionTypes.NOTIFICATION_REBOOT_SUCCESS_MESSAGE,
          message: '',
        });
    },

  getAssetStateNameCount:
    (name: string): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${AssetStatusActionTypes.REQUEST_ASSET_STATUSES_NAME_COUNT}`,
        'name: ' + name
      );

      dispatch({
        type: AssetStatusActionTypes.REQUEST_ASSET_STATUSES_NAME_COUNT,
        refreshing: true,
      });

      const response = await fetch(
        `assets/GetAssetStateNameCount/${name}`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as Map<
        string,
        AssetStateCountResponse[]
      >;
      console.log(
        `Dispatching ${AssetStatusActionTypes.RECEIVED_ASSET_STATUSES_NAME_COUNT}`
      );

      dispatch({
        type: AssetStatusActionTypes.RECEIVED_ASSET_STATUSES_NAME_COUNT,
        result: result,
      });
    },

  getNodeStatusCounts:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${AssetStatusActionTypes.REQUEST_NODE_STATUSES_COUNT}`
      );

      dispatch({
        type: AssetStatusActionTypes.REQUEST_NODE_STATUSES_COUNT,
        refreshing: true,
      });

      const response = await fetch(
        `assets/GetNodeStatusCounts`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as NodeStatusCount[];
      console.log(
        `Dispatching ${AssetStatusActionTypes.RECEIVED_NODE_STATUSES_COUNT}`,
        result
      );

      dispatch({
        type: AssetStatusActionTypes.RECEIVED_NODE_STATUSES_COUNT,
        result: result,
      });
    },

  getTopDeviceIssuesCountBySite:
    (rowsCount: number): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${AssetStatusActionTypes.REQUEST_SITE_ISSUES_COUNT}`
      );

      dispatch({
        type: AssetStatusActionTypes.REQUEST_SITE_ISSUES_COUNT,
        refreshing: true,
      });

      const response = await fetch(
        `assets/GetTopAssetStateNameCountsBySite/Status/${rowsCount}`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as AssetStateCountBySite[];
      console.log(
        `Dispatching ${AssetStatusActionTypes.RECEIVED_SITE_ISSUES_COUNT}`
      );

      dispatch({
        type: AssetStatusActionTypes.RECEIVED_SITE_ISSUES_COUNT,
        result: result,
      });
    },

  getTopLanesDownCountBySite:
    (rowsCount: number): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${AssetStatusActionTypes.REQUEST_SITE_LANES_DOWN_COUNT}`
      );

      dispatch({
        type: AssetStatusActionTypes.REQUEST_SITE_LANES_DOWN_COUNT,
        refreshing: true,
      });

      const response = await fetch(
        `assets/GetTopAssetStateNameCountsBySite/NodeAvailabilityStatus/${rowsCount}`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as AssetStateCountBySite[];
      console.log(
        `Dispatching ${AssetStatusActionTypes.RECEIVED_SITE_LANES_DOWN_COUNT}`
      );

      dispatch({
        type: AssetStatusActionTypes.RECEIVED_SITE_LANES_DOWN_COUNT,
        result: result,
      });
    },

  getTopAssetStateCountsByName:
    (name: string, rowsCount: number): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${AssetStatusActionTypes.REQUEST_CATEGORY_SUMMARY}`
      );

      dispatch({
        type: AssetStatusActionTypes.REQUEST_CATEGORY_SUMMARY,
        refreshing: true,
      });

      var result: CategorySummary[] = [];
      const response = await fetch(
        `assets/GetTopAssetStateCountsByName/${name}/${rowsCount}`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      result = (await response.json()) as CategorySummary[];

      dispatch({
        type: AssetStatusActionTypes.RECEIVED_CATEGORY_SUMMARY,
        categorySummary: result,
      });
    },

  getCurrentIssueCountsByDateRange:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${AssetStatusActionTypes.REQUEST_ASSET_STATE_PROPERTIES_COUNTS_BY_DATE_RANGE}`
      );

      dispatch({
        type: AssetStatusActionTypes.REQUEST_ASSET_STATE_PROPERTIES_COUNTS_BY_DATE_RANGE,
        refreshing: true,
      });

      let payload: TrendsFilterPayload = {
        AssetInfo: appState.userSettingsReducer.deviceTrendsFilter.AssetInfo,
        Filters: appState.userSettingsReducer.deviceTrendsFilter.Filters,
        StartDate: getDateWithoutTimezone(
          new Date(appState.userSettingsReducer.deviceTrendsFilter.StartDate)
        ),
        EndDate: getDateWithoutTimezone(
          new Date(appState.userSettingsReducer.deviceTrendsFilter.EndDate)
        ),
        DateRange: appState.userSettingsReducer.deviceTrendsFilter.DateRange,
      };

      const response = await fetch(
        `assets/GetCurrentIssueCountsByDateRange`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          payload
        )
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result =
        (await response.json()) as AssetStatePropertiesCountsByDateRange;

      console.log(
        `Dispatching ${AssetStatusActionTypes.RECEIVED_ASSET_STATE_PROPERTIES_COUNTS_BY_DATE_RANGE}`
      );

      result.DateRangeOption =
        appState.userSettingsReducer.deviceTrendsFilter.DateRange;

      dispatch({
        type: AssetStatusActionTypes.RECEIVED_ASSET_STATE_PROPERTIES_COUNTS_BY_DATE_RANGE,
        assetStatePropertiesCountsByDateRange: result,
      });
    },

  getCurrentIssueCountsByDate:
    (trendsGridFilter: TrendsGridFilter): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${AssetStatusActionTypes.REQUEST_ASSET_STATE_PROPERTIES_COUNTS_BY_DATE}`
      );

      dispatch({
        type: AssetStatusActionTypes.REQUEST_ASSET_STATE_PROPERTIES_COUNTS_BY_DATE,
        refreshing: true,
      });

      let payload: TrendsGridFilterPayload = {
        AssetInfo: trendsGridFilter.AssetInfo,
        Filters: trendsGridFilter.Filters,
        Date: getDateWithoutTimezone(trendsGridFilter.Date),
        StartDate: getDateWithoutTimezone(trendsGridFilter.StartDate),
        EndDate: getDateWithoutTimezone(trendsGridFilter.EndDate),
      };

      const response = await fetch(
        `assets/GetCurrentIssueCountsByDate`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          payload
        )
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as CurrentIssueCountsByDate;
      const converted = convertCurrentIssueCountsToCSV(result.Nodes);

      console.log(
        `Dispatching ${AssetStatusActionTypes.RECEIVED_ASSET_STATE_PROPERTIES_COUNTS_BY_DATE}`
      );

      dispatch({
        type: AssetStatusActionTypes.RECEIVED_ASSET_STATE_PROPERTIES_COUNTS_BY_DATE,
        currentIssueCountsByDate: converted,
        count: converted.length,
      });
    },

  getFirmwareInfo:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${AssetStatusActionTypes.REQUEST_FIRMWARE_INFO}`
      );

      dispatch({
        type: AssetStatusActionTypes.REQUEST_FIRMWARE_INFO,
      });

      const response = await fetch(
        `firmware/GetFirmwareInfo`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as FirmwareInfo[];

      console.log(
        `Dispatching ${AssetStatusActionTypes.RECEIVED_FIRMWARE_INFO}`
      );

      dispatch({
        type: AssetStatusActionTypes.RECEIVED_FIRMWARE_INFO,
        firmware: result,
      });
    },

  addNewFirmwareInfo:
    (firmwareInfo: MultipleFirmwareRecords): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${AssetStatusActionTypes.REQUEST_ADD_NEW_FIRMWARE_INFO}`
      );

      dispatch({
        type: AssetStatusActionTypes.REQUEST_ADD_NEW_FIRMWARE_INFO,
      });

      const response = await fetch(
        `firmware/addfirmware`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          firmwareInfo
        )
      ).catch(error => {
        console.error(error);
        dispatch({
          type: AssetStatusActionTypes.REGISTRATION_SUCCESS_MESSAGE,
          message: 'failed',
          filesUploaded: [],
        });
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) {
        dispatch({
          type: AssetStatusActionTypes.REGISTRATION_SUCCESS_MESSAGE,
          message: 'failed',
          filesUploaded: [],
        });
        return;
      }

      console.log(
        `Dispatching ${AssetStatusActionTypes.RECEIVED_ADD_NEW_FIRMWARE_INFO}`
      );
    },

  updateFirmwareFile:
    (formData: FormData): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${AssetStatusActionTypes.REQUEST_UPDATE_FIRMWARE_FILE}`
      );

      dispatch({
        type: AssetStatusActionTypes.REQUEST_UPDATE_FIRMWARE_FILE,
      });

      const response = await fetch(
        `firmware/updatefile`,
        postFormRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          formData
        )
      ).catch(error => {
        console.error(error);
        dispatch({
          type: AssetStatusActionTypes.UPDATE_FILE_SUCCESS_MESSAGE,
          message: 'failed',
        });
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) {
        dispatch({
          type: AssetStatusActionTypes.UPDATE_FILE_SUCCESS_MESSAGE,
          message: 'failed',
        });
        return;
      }

      console.log(
        `Dispatching ${AssetStatusActionTypes.RECEIVED_UPDATE_FIRMWARE_FILE}`
      );

      dispatch({
        type: AssetStatusActionTypes.UPDATE_FILE_SUCCESS_MESSAGE,
        message: 'success',
      });
    },

  removeFirmwareInfo:
    (firmwareInfo: FirmwareInfo): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${AssetStatusActionTypes.REQUEST_REMOVE_FIRMWARE_INFO}`
      );

      dispatch({
        type: AssetStatusActionTypes.REQUEST_REMOVE_FIRMWARE_INFO,
      });

      const response = await fetch(
        `firmware/remove`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          firmwareInfo
        )
      ).catch(error => {
        console.error(error);
        dispatch({
          type: AssetStatusActionTypes.DELETE_FILE_SUCCESS_MESSAGE,
          message: 'failed',
        });
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) {
        dispatch({
          type: AssetStatusActionTypes.DELETE_FILE_SUCCESS_MESSAGE,
          message: 'failed',
        });
        return;
      }

      const result = (await response.json()) as FirmwareInfo;

      console.log(
        `Dispatching ${AssetStatusActionTypes.RECEIVED_REMOVE_FIRMWARE_INFO}`
      );

      dispatch({
        type: AssetStatusActionTypes.DELETE_FILE_SUCCESS_MESSAGE,
        message: 'success',
      });

      dispatch({
        type: AssetStatusActionTypes.RECEIVED_REMOVE_FIRMWARE_INFO,
        firmware: result,
      });
    },

  getCurrentIssuePropertiesCSV:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${AssetStatusActionTypes.REQUEST_ASSET_STATE_PROPERTIES_DETAILS_BY_DATE_RANGE}`
      );

      dispatch({
        type: AssetStatusActionTypes.REQUEST_ASSET_STATE_PROPERTIES_DETAILS_BY_DATE_RANGE,
        refreshing: true,
      });

      let payload: TrendsFilterPayload = {
        AssetInfo: appState.userSettingsReducer.deviceTrendsFilter.AssetInfo,
        Filters: appState.userSettingsReducer.deviceTrendsFilter.Filters,
        StartDate: getDateWithoutTimezone(
          new Date(appState.userSettingsReducer.deviceTrendsFilter.StartDate)
        ),
        EndDate: getDateWithoutTimezone(
          new Date(appState.userSettingsReducer.deviceTrendsFilter.EndDate)
        ),
        DateRange: appState.userSettingsReducer.deviceTrendsFilter.DateRange,
      };

      const response = await fetch(
        `assets/GetCurrentIssueDetailsByDateRange`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          payload
        )
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as CurrentIssuePropertiesCSV[];

      console.log(
        `Dispatching ${AssetStatusActionTypes.RECEIVED_ASSET_STATE_PROPERTIES_COUNTS_BY_DATE_RANGE}`
      );

      dispatch({
        type: AssetStatusActionTypes.RECEIVED_ASSET_STATE_PROPERTIES_DETAILS_BY_DATE_RANGE,
        data: result,
      });
    },

  uploadFirmwareFile:
    (formData: FormData | undefined): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;
      console.log(
        `Dispatching ${AssetStatusActionTypes.REQUEST_UPLOAD_FIRMWARE_FILE}`
      );

      const response = await fetch(
        `firmware/UploadFile`,
        postFormRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          formData
        )
      ).catch(error => {
        console.error(error);
        dispatch({
          type: AssetStatusActionTypes.REGISTRATION_SUCCESS_MESSAGE,
          message: 'failed',
          filesUploaded: [],
        });
        return Promise.reject(error);
      });
      if (!isResponseOk(response, dispatch)) {
        dispatch({
          type: AssetStatusActionTypes.REGISTRATION_SUCCESS_MESSAGE,
          message: 'failed',
          filesUploaded: [],
        });
        return;
      }

      const result = (await response.json()) as string[];

      if (result.length === 0) {
        dispatch({
          type: AssetStatusActionTypes.REGISTRATION_SUCCESS_MESSAGE,
          message: 'failed',
          filesUploaded: [],
        });
        return;
      }

      dispatch({
        type: AssetStatusActionTypes.REGISTRATION_SUCCESS_MESSAGE,
        message: 'success',
        filesUploaded: result,
      });
    },

  calculateChecksum:
    (formData: FormData): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;
      console.log(
        `Dispatching ${AssetStatusActionTypes.REQUEST_FILE_CHECKSUM}`
      );

      dispatch({ type: AssetStatusActionTypes.REQUEST_FILE_CHECKSUM });

      const response = await fetch(
        `firmware/CalculateChecksum`,
        postFormRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          formData
        )
      ).catch(error => {
        console.error(error);
        dispatch({
          type: AssetStatusActionTypes.RECEIVED_FILE_CHECKSUM,
          fileChecksumMap: [],
        });
        return Promise.reject(error);
      });
      if (!isResponseOk(response, dispatch)) {
        dispatch({
          type: AssetStatusActionTypes.RECEIVED_FILE_CHECKSUM,
          fileChecksumMap: [],
        });
        return;
      }

      const result = (await response.json()) as FirmwareFileAndChecksumMap[];

      console.log(
        `Dispatching ${AssetStatusActionTypes.RECEIVED_FILE_CHECKSUM}`
      );

      dispatch({
        type: AssetStatusActionTypes.UPDATE_CONFIRMATION_MESSAGE,
        message: 'add',
      });

      dispatch({
        type: AssetStatusActionTypes.RECEIVED_FILE_CHECKSUM,
        fileChecksumMap: result,
      });
    },

  clearConfirmationMessage:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(`Dispatching ${AssetStatusActionTypes.CLEAR_CHECKSUM}`);
      dispatch({
        type: AssetStatusActionTypes.RECEIVED_FILE_CHECKSUM,
        fileChecksumMap: [],
      });
    },

  clearChecksum:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(`Dispatching ${AssetStatusActionTypes.CLEAR_CHECKSUM}`);
      dispatch({
        type: AssetStatusActionTypes.CLEAR_CHECKSUM,
        fileChecksumMap: [],
      });
    },
  resetSuccessMessage:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${AssetStatusActionTypes.REGISTRATION_SUCCESS_MESSAGE}`
      );
      dispatch({
        type: AssetStatusActionTypes.REGISTRATION_SUCCESS_MESSAGE,
        message: '',
        filesUploaded: [],
      });
    },
  resetUpdateMessage:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${AssetStatusActionTypes.UPDATE_FILE_SUCCESS_MESSAGE}`
      );
      dispatch({
        type: AssetStatusActionTypes.UPDATE_FILE_SUCCESS_MESSAGE,
        message: '',
      });
    },
  resetDeleteMessage:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${AssetStatusActionTypes.DELETE_FILE_SUCCESS_MESSAGE}`
      );
      dispatch({
        type: AssetStatusActionTypes.DELETE_FILE_SUCCESS_MESSAGE,
        message: '',
      });
    },
};

const initialAssetStatePropertiesCountsByDateRange: AssetStatePropertiesCountsByDateRange =
  {
    AssetInfo: { Category: '', Name: '', Value: '' },
    Counts: [],
    StartDate: new Date(),
    EndDate: new Date(),
    DateRangeOption: ApiEnums.DateRangeOptions.LastSevenDays,
  };

const initialAssetStatePropertiesCountsByDate: DeviceIssueTrendsCSV[] = [];

// ----------------
// REDUCER - For a given state and action, returns the new state. To support time travel, this must not mutate the old state.
export const unloadedState: AssetStatusesState = {
  unauthorizedRequest: false,
  isFetchingAssetStatuses: false,
  assetStatuses: [],
  currentIssueCategories: [],
  isFetchingCurrentIssueCategories: false,
  currentIssueValues: new Map(),
  isFetchingCurrentIssueValues: false,
  fetchError: { StatusCode: 0, Message: '' },
  notificationSuccessMessage: '',
  isFetchingAssetStatusCounts: false,
  assetStatusCounts: new Map(),
  isFetchingNodeStatusCounts: false,
  nodeStatusCounts: [],
  isFetchingDeviceIssuesCountBySite: false,
  deviceIssuesCountBySite: [],
  isFetchingLanesDownCountBySite: false,
  lanesDownCountBySite: [],
  isFetchingCategorySummary: false,
  categorySummary: [],
  isFetchingAssetStatusesByNode: false,
  assetStateProperties: [],
  isFetchingAssetStatePropertiesCountsByDateRange: false,
  assetStatePropertiesCountsByDateRange:
    initialAssetStatePropertiesCountsByDateRange,
  isFetchingCurrentIssueCountsByDate: true,
  currentIssuesByDate: initialAssetStatePropertiesCountsByDate,
  currentIssueCount: 0,
  notificationRebootSuccessMessage: '',
  firmwareInfo: [],
  isFetchingCurrentIssuePropertiesCSVData: false,
  currentIssuePropertiesCSVData: [],
  fileChecksum: [],
  fileChecksumMap: [],
  filesUploaded: [],
  confirmationMessage: '',
  registrationSuccessMessage: '',
  updateSuccessMessage: '',
  deleteSuccessMessage: '',
};

export const reducer: Reducer<AssetStatusesState> = (
  state: AssetStatusesState | undefined,
  incomingAction: Action
): AssetStatusesState => {
  if (state === undefined) {
    return unloadedState;
  }

  const action = incomingAction as KnownAction;
  switch (action.type) {
    case AssetStatusActionTypes.RESET_STATE:
      return unloadedState;

    case AssetStatusActionTypes.UNAUTHORIZED_REQUEST:
      return {
        ...state,
        unauthorizedRequest: true,
      };

    case AssetStatusActionTypes.REQUEST_NODE_ASSET_STATUSES:
      return {
        ...state,
        assetStatuses: [],
        isFetchingAssetStatuses: true,
      };

    case AssetStatusActionTypes.REQUEST_NODE_ASSET_STATUSES_BY_NODE:
      return {
        ...state,
        assetStateProperties: [],
        isFetchingAssetStatusesByNode: true,
      };

    case AssetStatusActionTypes.RECEIVED_NODE_ASSET_STATUSES_BY_NODE:
      return {
        ...state,
        isFetchingAssetStatusesByNode: false,
        assetStateProperties: action.assetStateProperties,
      };

    case AssetStatusActionTypes.RECEIVED_NODE_ASSET_STATUSES_BY_QUERY:
      return {
        ...state,
        isFetchingAssetStatuses: false,
        assetStatuses: action.result.Result,
      };

    case AssetStatusActionTypes.RECEIVED_CURRENT_ISSUE_CATEGORIES:
      return {
        ...state,
        currentIssueCategories: action.categories,
        isFetchingCurrentIssueCategories: false,
      };
    case AssetStatusActionTypes.REQUEST_CURRENT_ISSUE_CATEGORIES:
      return {
        ...state,
        isFetchingCurrentIssueCategories: action.refreshing,
      };
    case AssetStatusActionTypes.RECEIVED_CURRENT_ISSUE_VALUES:
      let values = new Map(state.currentIssueValues);

      if (action.values != null && action.categoryCountRequest != undefined) {
        values.set(action.categoryCountRequest, action.values);
      }

      return {
        ...state,
        currentIssueValues: values,
        isFetchingCurrentIssueValues: false,
      };
    case AssetStatusActionTypes.REQUEST_CURRENT_ISSUE_VALUES:
      return {
        ...state,
        isFetchingCurrentIssueValues: action.refreshing,
      };

    case AssetStatusActionTypes.RECEIVED_HTTP_RESPONSE_ERROR_CODE:
      let fetchErrorData = {
        StatusCode: action.statusCode,
        Message: 'FETCH_ERROR_MESSAGE_ASSET_STATUSES',
      };
      return {
        ...state,
        isFetchingAssetStatuses: false,
        fetchError: fetchErrorData,
        isFetchingCurrentIssueCategories: false,
      };
    case AssetStatusActionTypes.RESET_HTTP_RESPONSE_FETCH_ERROR:
      let resetData = { StatusCode: 0, Message: '' };
      return {
        ...state,
        fetchError: resetData,
      };

    case AssetStatusActionTypes.NOTIFICATION_SUCCESS_MESSAGE:
      return {
        ...state,
        notificationSuccessMessage: action.message,
      };

    case AssetStatusActionTypes.REQUEST_ASSET_STATUSES_NAME_COUNT:
      return {
        ...state,
        assetStatusCounts: new Map(),
        isFetchingAssetStatusCounts: action.refreshing,
      };

    case AssetStatusActionTypes.RECEIVED_ASSET_STATUSES_NAME_COUNT:
      return {
        ...state,
        assetStatusCounts: action.result,
        isFetchingAssetStatusCounts: false,
      };

    case AssetStatusActionTypes.REQUEST_NODE_STATUSES_COUNT:
      return {
        ...state,
        nodeStatusCounts: [],
        isFetchingNodeStatusCounts: action.refreshing,
      };

    case AssetStatusActionTypes.RECEIVED_NODE_STATUSES_COUNT:
      return {
        ...state,
        nodeStatusCounts: action.result,
        isFetchingNodeStatusCounts: false,
      };

    case AssetStatusActionTypes.REQUEST_SITE_ISSUES_COUNT:
      return {
        ...state,
        isFetchingDeviceIssuesCountBySite: action.refreshing,
      };

    case AssetStatusActionTypes.RECEIVED_SITE_ISSUES_COUNT:
      return {
        ...state,
        isFetchingDeviceIssuesCountBySite: false,
        deviceIssuesCountBySite: action.result,
      };

    case AssetStatusActionTypes.REQUEST_SITE_LANES_DOWN_COUNT:
      return {
        ...state,
        isFetchingLanesDownCountBySite: action.refreshing,
      };

    case AssetStatusActionTypes.RECEIVED_SITE_LANES_DOWN_COUNT:
      return {
        ...state,
        lanesDownCountBySite: action.result,
        isFetchingLanesDownCountBySite: false,
      };

    case AssetStatusActionTypes.REQUEST_CATEGORY_SUMMARY:
      return {
        ...state,
        isFetchingCategorySummary: action.refreshing,
      };

    case AssetStatusActionTypes.RECEIVED_CATEGORY_SUMMARY:
      return {
        ...state,
        categorySummary: action.categorySummary,
        isFetchingCategorySummary: false,
      };

    case AssetStatusActionTypes.REQUEST_ASSET_STATE_PROPERTIES_COUNTS_BY_DATE_RANGE:
      return {
        ...state,
        isFetchingAssetStatePropertiesCountsByDateRange: action.refreshing,
      };

    case AssetStatusActionTypes.RECEIVED_ASSET_STATE_PROPERTIES_COUNTS_BY_DATE_RANGE:
      return {
        ...state,
        assetStatePropertiesCountsByDateRange:
          action.assetStatePropertiesCountsByDateRange,
        isFetchingAssetStatePropertiesCountsByDateRange: false,
      };

    case AssetStatusActionTypes.REQUEST_ASSET_STATE_PROPERTIES_COUNTS_BY_DATE:
      return {
        ...state,
        isFetchingCurrentIssueCountsByDate: action.refreshing,
        currentIssuesByDate: initialAssetStatePropertiesCountsByDate,
        currentIssueCount: 0,
      };

    case AssetStatusActionTypes.RECEIVED_ASSET_STATE_PROPERTIES_COUNTS_BY_DATE:
      return {
        ...state,
        currentIssuesByDate: action.currentIssueCountsByDate,
        currentIssueCount: action.count,
        isFetchingCurrentIssueCountsByDate: false,
      };

    case AssetStatusActionTypes.NOTIFICATION_REBOOT_SUCCESS_MESSAGE:
      return {
        ...state,
        notificationRebootSuccessMessage: action.message,
      };

    case AssetStatusActionTypes.RECEIVED_FIRMWARE_INFO:
      return {
        ...state,
        firmwareInfo: action.firmware,
      };

    case AssetStatusActionTypes.REQUEST_ADD_NEW_FIRMWARE_INFO:
      return {
        ...state,
      };

    case AssetStatusActionTypes.RECEIVED_ADD_NEW_FIRMWARE_INFO:
      return {
        ...state,
        firmwareInfo: [...state.firmwareInfo, action.firmware],
      };

    case AssetStatusActionTypes.REQUEST_UPDATE_FIRMWARE_FILE:
      return {
        ...state,
      };

    case AssetStatusActionTypes.RECEIVED_UPDATE_FIRMWARE_FILE:
      return {
        ...state,
        firmwareInfo: state.firmwareInfo.map(item =>
          item.DeviceCategory === action.firmware.DeviceCategory &&
          item.DeviceModel === action.firmware.DeviceModel &&
          item.RegionInfo === action.firmware.RegionInfo
            ? { ...item, FileName: action.firmware.FileName }
            : item
        ),
      };

    case AssetStatusActionTypes.REQUEST_REMOVE_FIRMWARE_INFO:
      return {
        ...state,
      };

    case AssetStatusActionTypes.RECEIVED_REMOVE_FIRMWARE_INFO:
      return {
        ...state,
        firmwareInfo: state.firmwareInfo.filter(
          item =>
            item.DeviceCategory !== action.firmware.DeviceCategory ||
            item.DeviceModel !== action.firmware.DeviceModel ||
            item.RegionInfo !== action.firmware.RegionInfo
        ),
      };

    case AssetStatusActionTypes.REQUEST_FILE_CHECKSUM:
      return {
        ...state,
      };

    case AssetStatusActionTypes.RECEIVED_FILE_CHECKSUM:
      return {
        ...state,
        fileChecksumMap: action.fileChecksumMap,
      };

    case AssetStatusActionTypes.UPDATE_CONFIRMATION_MESSAGE:
      return {
        ...state,
        confirmationMessage: action.message,
      };

    case AssetStatusActionTypes.CLEAR_CONFIRMATION_MESSAGE:
      return {
        ...state,
        confirmationMessage: action.message,
      };

    case AssetStatusActionTypes.CLEAR_CHECKSUM:
      return {
        ...state,
        fileChecksumMap: action.fileChecksumMap,
      };

    case AssetStatusActionTypes.REGISTRATION_SUCCESS_MESSAGE:
      return {
        ...state,
        registrationSuccessMessage: action.message,
        filesUploaded: action.filesUploaded,
      };

    case AssetStatusActionTypes.UPDATE_FILE_SUCCESS_MESSAGE:
      return {
        ...state,
        updateSuccessMessage: action.message,
      };

    case AssetStatusActionTypes.DELETE_FILE_SUCCESS_MESSAGE:
      return {
        ...state,
        deleteSuccessMessage: action.message,
      };

    case AssetStatusActionTypes.REQUEST_ASSET_STATE_PROPERTIES_DETAILS_BY_DATE_RANGE:
      return {
        ...state,
        isFetchingCurrentIssuePropertiesCSVData: action.refreshing,
      };

    case AssetStatusActionTypes.RECEIVED_ASSET_STATE_PROPERTIES_DETAILS_BY_DATE_RANGE:
      return {
        ...state,
        currentIssuePropertiesCSVData: action.data,
        isFetchingCurrentIssuePropertiesCSVData: false,
      };

    default:
      break;
  }
  return state;
};
